import {
	Box,
	Checkbox,
	FormControlLabel,
	MenuItem,
	TextField,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import {
	CHART_COLORS,
	type ChartDisplayMode,
	type GroupRow,
	type GroupRowBase,
} from "../models/InformationRetrieval";
import { buildChartSeries } from "../utils/aggregate";

export type ChartSeries = ReturnType<typeof buildChartSeries>;

// Module-level so the default does not change identity on every render.
const MILL_SERIES_NAMES = { target: "Recipe", actual: "RFactor" };

interface Props {
	groups: GroupRowBase[];
	selectedKeys: string[];
	stands: string[];
	/** Precomputed aggregate lines. Left out, the mill aggregate is used. */
	aggregate?: ChartSeries;
	/** Legend names of the target and actual lines. */
	seriesNames?: { target: string; actual: string };
	/** Fit the Y axis to the data instead of the mill's fixed 0.8-1.7 window. */
	autoScaleY?: boolean;
	displayMode: ChartDisplayMode;
	onDisplayModeChange: (mode: ChartDisplayMode) => void;
	rangeEnabled: boolean;
	onRangeEnabledChange: (value: boolean) => void;
	recipeRangeEnabled: boolean;
	onRecipeRangeEnabledChange: (value: boolean) => void;
}

export const InformationRetrievalChart = ({
	groups,
	selectedKeys,
	stands,
	aggregate: aggregateProp,
	seriesNames = MILL_SERIES_NAMES,
	autoScaleY = false,
	displayMode,
	onDisplayModeChange,
	rangeEnabled,
	onRangeEnabledChange,
	recipeRangeEnabled,
	onRecipeRangeEnabledChange,
}: Props) => {
	const option = useMemo(() => {
		const aggregate =
			aggregateProp ??
			buildChartSeries(groups as GroupRow[], selectedKeys, stands);

		const line = (
			name: string,
			data: (number | null)[],
			color: string,
			width = 2,
		) => ({
			name,
			type: "line",
			data,
			itemStyle: { color },
			lineStyle: { color, width },
			symbolSize: 5,
			connectNulls: true,
		});

		const series = [
			line(seriesNames.target, aggregate.target, CHART_COLORS.target),
		];

		if (displayMode === "average") {
			series.push(
				line(seriesNames.actual, aggregate.actual, CHART_COLORS.actual),
			);
		}

		if (rangeEnabled) {
			series.push(
				line("Min", aggregate.minActual, CHART_COLORS.band, 1),
				line("Max", aggregate.maxActual, CHART_COLORS.band, 1),
			);
		}

		if (recipeRangeEnabled) {
			series.push(
				line("Recipe Min", aggregate.minRecipe, CHART_COLORS.recipeBand, 1),
				line("Recipe Max", aggregate.maxRecipe, CHART_COLORS.recipeBand, 1),
			);
		}

		// Straight mode draws what each selected date/shift ran on its own,
		// which is the comparison the operator opens this screen for.
		if (displayMode === "straight") {
			for (const key of selectedKeys) {
				const group = groups.find((g) => g.key === key);
				if (!group) continue;
				series.push(
					line(
						group.label,
						stands.map((s) => group.stands[s]?.avg ?? null),
						group.legendColor,
					),
				);
			}
		}

		return {
			animation: true,
			animationDuration: 0,
			animationDurationUpdate: 400,
			tooltip: {
				trigger: "axis",
				valueFormatter: (v: number | null) =>
					v == null ? "—" : Number(v).toFixed(3),
			},
			legend: { type: "scroll", bottom: 0, textStyle: { fontSize: 11 } },
			grid: { left: 52, right: 24, top: 12, bottom: 46 },
			xAxis: {
				type: "category",
				data: stands,
				axisLabel: { fontSize: 11 },
			},
			// Fixed 0.8–1.7 window, as in the legacy chart. Values below it are
			// artifacts of the legacy aggregation — `Σ RFactorMinSum / Σ BilletsCount`
			// divides a sum that only exists for billets carrying stand data by
			// *every* billet in the selection, so a partially-tracked stand reads
			// ~0.3 and a selection with no recipe reads 0. Letting the axis stretch
			// to include them was measured and is worse: the 1.0–1.4 band where the
			// mill actually runs collapses into a sliver. The window stays.
			yAxis: autoScaleY
				? {
						type: "value",
						scale: true,
						axisLabel: { formatter: (v: number) => v.toFixed(3) },
					}
				: {
						type: "value",
						min: 0.8,
						max: 1.7,
						interval: 0.1,
						axisLabel: { formatter: (v: number) => v.toFixed(3) },
					},
			series,
		};
	}, [
		groups,
		selectedKeys,
		stands,
		aggregateProp,
		seriesNames,
		autoScaleY,
		displayMode,
		rangeEnabled,
		recipeRangeEnabled,
	]);

	return (
		<Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
			<Box sx={{ width: 130, flexShrink: 0, pt: 1 }}>
				<TextField
					select
					size="small"
					label="Display"
					fullWidth
					value={displayMode}
					onChange={(e) =>
						onDisplayModeChange(e.target.value as ChartDisplayMode)
					}
				>
					<MenuItem value="average">Average</MenuItem>
					<MenuItem value="straight">Straight</MenuItem>
				</TextField>
				<FormControlLabel
					control={
						<Checkbox
							size="small"
							checked={rangeEnabled}
							onChange={(e) => onRangeEnabledChange(e.target.checked)}
						/>
					}
					label="Min/Max"
					slotProps={{ typography: { fontSize: "0.85rem" } }}
				/>
				<FormControlLabel
					control={
						<Checkbox
							size="small"
							checked={recipeRangeEnabled}
							onChange={(e) => onRecipeRangeEnabledChange(e.target.checked)}
						/>
					}
					label="Recipe"
					slotProps={{ typography: { fontSize: "0.85rem" } }}
				/>
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<ReactECharts
					option={option}
					notMerge
					lazyUpdate
					style={{ height: 300, width: "100%" }}
				/>
			</Box>
		</Box>
	);
};
